import PlotTypeSelector from "components/charts/modern/inputs/PlotTypeSelector";
import DividendsPaid from "components/company/v3/contents/events/dividends/DividendsPaid";
import DividendsPaidByYear from "components/company/v3/contents/events/dividends/DividendsPaidByYear";
import DividendsPaidByYearChart from "components/company/v3/contents/events/dividends/DividendsPaidByYearChart";
import DividendsPaidChart from "components/company/v3/contents/events/dividends/DividendsPaidChart";
import DividendsSummary from "components/company/v3/contents/events/dividends/DividendsSummary";
import MandatoryDividend from "components/company/v3/contents/events/dividends/MandatoryDividend";
import type { TDividendsPaidByYear } from "components/company/v3/contents/events/dividends/types";
import SelectFilterByYear from "components/company/v3/SelectFilterByYear";
import getDividendYield from "components/company/v3/utils/getDividendYield";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import classNames from "components/ui/classNames";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import getYear from "date-fns/getYear";
import parseISO from "date-fns/parseISO";
import parseDateYmd from "lib/utils/parseDateYmd";
import { BarChartIcon, TableIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type {
  IBrazilianCompanyIssuerMandatoryDividend,
  IDividendsPaidDetails,
  IDividendYieldDetails,
} from "types/graphqlTypes";

interface DividendsContentProps {
  tickerSymbol: string;
  currency: "BRL" | "USD";
  loading: boolean;
  filterDividendsByYear: number | null;
  setFilterDividendsByYear: (year: number | null) => void;
  data: IDividendsPaidDetails[];
  availableYears: number[];
  dividendYieldSnapshotEachSymbol: IDividendYieldDetails[];
  mandatoryDividend?: Pick<
    IBrazilianCompanyIssuerMandatoryDividend,
    "displayText" | "periodicity" | "periodicityDescription" | "remarks" | "hasPreferredSharesDividendAdvantage"
  > | null;
  isRenderPageDefault?: boolean;
  setEnabledFullHeight: (enabled: boolean) => void;
}

export default function DividendsContent({
  tickerSymbol,
  currency,
  loading,
  filterDividendsByYear,
  setFilterDividendsByYear,
  data,
  availableYears,
  dividendYieldSnapshotEachSymbol,
  mandatoryDividend,
  isRenderPageDefault = false,
  setEnabledFullHeight,
}: DividendsContentProps) {
  const [groupedByYear, setGroupedByYear] = useState(false);
  const [plotType, setPlotType] = useState<"column" | "table">("table");

  const intl = useIntl();

  const dividendsPaid =
    currency === "BRL"
      ? data
          .filter((node) => filterActionsByTickerSymbol(tickerSymbol, node))
          .filter((node) => getYear(parseISO(node.recordDate)) >= 1995)
      : data;
  const hasPayDate = dividendsPaid.some((node) => Boolean(node.payDate));

  const dividendsPaidByYear = useMemo(() => {
    return dividendsPaid.reduce((dividendsByYear, node) => {
      const groupingDate = hasPayDate ? node.recordDate : node.excludingDate;
      const year = getYear(parseDateYmd(groupingDate));
      const candidate = dividendsByYear.get(year);

      if (!candidate) {
        dividendsByYear.set(year, {
          year,
          dividendsPaid: [node],
          amountPaid: node.amountPaid,
        });
      } else {
        dividendsByYear.set(year, {
          year,
          dividendsPaid: [...candidate.dividendsPaid, node],
          amountPaid: candidate.amountPaid + node.amountPaid,
        });
      }

      return dividendsByYear;
    }, new Map<number, TDividendsPaidByYear>());
  }, [dividendsPaid, hasPayDate]);

  if (loading && !data) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  if (availableYears.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600 dark:text-slate-400">
          <SadFaceIcon />
          <p>
            <FormattedMessage defaultMessage="Nenhum dado encontrado." id="waxTCd" />
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col items-end space-y-4">
      <div className="flex w-full flex-col items-center justify-between gap-4 sm:flex-row">
        <div className="grid w-full grid-cols-2 items-center justify-start gap-4 lg:flex">
          <div className="w-full md:w-36">
            <SelectFilterByYear
              value={filterDividendsByYear}
              onChange={setFilterDividendsByYear}
              availableYears={availableYears}
              disabled={currency === "BRL" ? groupedByYear : undefined}
            />
          </div>

          <button
            type="button"
            className={classNames(
              "h-10 rounded-lg px-4 py-2 text-sm font-medium text-slate-600",
              "leading-5 tracking-normal",
              "border border-slate-200 bg-slate-50",
              "hover:bg-indigo-50 hover:text-indigo-600",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-300 focus-visible:ring-offset-2",
              "focus-visible:border-transparent",
              "disabled:cursor-not-allowed",
              {
                "bg-indigo-50 text-indigo-600": groupedByYear,
              },
              "dark:border-slate-800 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-indigo-900/10 dark:hover:text-indigo-400",
            )}
            onClick={() => {
              if (groupedByYear === false) {
                setGroupedByYear(true);
                setFilterDividendsByYear(null);
              } else {
                setGroupedByYear(false);
              }
            }}
          >
            <FormattedMessage defaultMessage="Agrupar por ano" id="wE4cTx" />
          </button>

          <div
            className={classNames({
              "col-span-2": groupedByYear,
            })}
          >
            <PlotTypeSelector
              types={[
                {
                  type: "table",
                  label: intl.formatMessage({
                    id: "jZHHzp",
                    defaultMessage: "Tabela",
                    description: "Tipo de visualização do gráfico",
                  }),
                  icon: <TableIcon className="h-5 w-5" />,
                },
                {
                  type: "column",
                  label: intl.formatMessage({
                    id: "hZ073v",
                    defaultMessage: "Barra",
                    description: "Tipo de visualização do gráfico",
                  }),
                  icon: <BarChartIcon className="h-5 w-5" />,
                },
              ]}
              value={plotType}
              onChange={(value) => {
                setPlotType(value);
                if (value === "column") {
                  setEnabledFullHeight(true);
                } else {
                  setEnabledFullHeight(false);
                }
              }}
            />
          </div>

          {!groupedByYear && dividendYieldSnapshotEachSymbol.length > 0 && (
            <SimpleTextTooltip
              content={<FormattedMessage defaultMessage="Dividend Yield dos últimos 12 meses" id="z30HiR" />}
            >
              <div
                className={classNames(
                  "flex justify-center whitespace-nowrap rounded-lg px-4 py-2 text-sm text-slate-600 lg:hidden",
                  "bg-yellow-600/10 font-bold text-yellow-600",
                )}
              >
                DY {getDividendYield(dividendYieldSnapshotEachSymbol, tickerSymbol)}%
              </div>
            </SimpleTextTooltip>
          )}
        </div>

        {groupedByYear && (
          <p className="whitespace-nowrap text-xs italic tracking-wider">
            *
            {currency === "BRL" && hasPayDate ? (
              <FormattedMessage defaultMessage="Valores baseados na Data-com dos proventos." id="CuLis2" />
            ) : (
              <FormattedMessage defaultMessage="Valores baseados na data-ex de pagamento dos dividendos." id="fdvV7R" />
            )}
          </p>
        )}

        {!groupedByYear && dividendYieldSnapshotEachSymbol.length > 0 && (
          <SimpleTextTooltip
            content={<FormattedMessage defaultMessage="Dividend Yield dos últimos 12 meses" id="z30HiR" />}
          >
            <div
              className={classNames(
                "hidden whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-slate-600 lg:flex",
                "bg-yellow-600/10 font-bold text-yellow-600",
              )}
            >
              DY {getDividendYield(dividendYieldSnapshotEachSymbol, tickerSymbol)}%
            </div>
          </SimpleTextTooltip>
        )}
      </div>

      <div className="flex h-full w-full flex-col items-end space-y-4">
        {plotType === "table" && !groupedByYear && (
          <DividendsPaid
            dividendsPaid={dividendsPaid}
            currency={currency}
            hasPayDate={hasPayDate}
          />
        )}

        {plotType === "table" && groupedByYear && (
          <DividendsPaidByYear
            dividendsPaid={Array.from(dividendsPaidByYear.values())}
            currency={currency}
            isRenderPageDefault={isRenderPageDefault}
          />
        )}

        {plotType === "column" && groupedByYear && (
          <DividendsPaidByYearChart dividendsPaid={Array.from(dividendsPaidByYear.values())} currency={currency} />
        )}

        {plotType === "column" && !groupedByYear && (
          <DividendsPaidChart dividendsPaid={dividendsPaid} currency={currency} />
        )}

        {false && filterDividendsByYear === null && (
          <DividendsSummary
            tickerSymbol={tickerSymbol}
            currency={currency}
            dividendsPaid={dividendsPaid}
            dividendYieldSnapshotEachSymbol={dividendYieldSnapshotEachSymbol}
          />
        )}

        {mandatoryDividend && <MandatoryDividend mandatoryDividend={mandatoryDividend} />}
      </div>
    </div>
  );
}

function filterActionsByTickerSymbol(tickerSymbol: string | null, node: IDividendsPaidDetails) {
  if (tickerSymbol && tickerSymbol === node.tickerSymbol) {
    return true;
  }

  if (!tickerSymbol) {
    return true;
  }

  return false;
}
